import { useTranslation } from 'react-i18next';
import { Button } from 'shared-ui/lib/components/Button';
import { TableColumn } from 'shared-ui/lib/customs/react/generic-table/TableColumn';
import { formatDate, formatDateTime } from 'shared-ui/lib/utils/format-date';
import { PartnerGroupOption } from '../components/PartnerGroupsModal';
import { MyActivePartnershipsQuery, MyIncomingPartnerInvitesQuery } from '../generated/graphql';

type InviteRow = MyIncomingPartnerInvitesQuery['myIncomingPartnerInvites'][0];
type PartnershipRow = MyActivePartnershipsQuery['myActivePartnerships'][0];

const InviteActions = ({
  invite,
  onAccept,
  onReject,
}: {
  invite: InviteRow;
  onAccept: (invite: InviteRow) => void;
  onReject: (invite: InviteRow) => void;
}): JSX.Element => {
  const { t } = useTranslation('PartnershipScene');

  return (
    <div className="flex space-x-2">
      <Button type="button" onClick={() => onAccept(invite)}>
        {t('accept')}
      </Button>
      <Button type="button" onClick={() => onReject(invite)}>
        {t('reject')}
      </Button>
    </div>
  );
};

const PartnershipActions = ({
  partnership,
  onEditGroups,
  onEnd,
}: {
  partnership: PartnershipRow;
  onEditGroups: (partnership: PartnershipRow) => void;
  onEnd: (partnership: PartnershipRow) => void;
}): JSX.Element => {
  const { t } = useTranslation('PartnershipScene');

  return (
    <div className="flex space-x-2">
      <Button type="button" onClick={() => onEditGroups(partnership)}>
        {t('editGroups')}
      </Button>
      <Button type="button" onClick={() => onEnd(partnership)}>
        {t('endPartnership')}
      </Button>
    </div>
  );
};

const partnerName: TableColumn = {
  field: 'partner.name',
  text: 'PartnershipScene:partnerName',
};

const invitedAt: TableColumn = {
  field: 'updatedAt',
  text: 'PartnershipScene:createdAt',
  formatter: (data: InviteRow) => formatDateTime(data.updatedAt),
};

const startedAt: TableColumn = {
  field: 'startedAt',
  text: 'PartnershipScene:startedAt',
  default: '-',
  formatter: (data: PartnershipRow) => (data.startedAt ? formatDate(data.startedAt) : '-'),
};

const groupsSummary = (groups: PartnerGroupOption[]): TableColumn => ({
  field: 'allowedGroupIds',
  text: 'PartnershipScene:groups',
  formatter: (data: PartnershipRow) => {
    const names = groups
      .filter((group) => (data.allowedGroupIds ?? []).includes(group.id))
      .map((group) => group.name);
    return names.length > 0 ? names.join(', ') : '-';
  },
});

const inviteActions = (
  onAccept: (invite: InviteRow) => void,
  onReject: (invite: InviteRow) => void
): TableColumn => ({
  field: 'id',
  text: 'PartnershipScene:actions',
  formatter: (data: InviteRow) => (
    <InviteActions invite={data} onAccept={onAccept} onReject={onReject} />
  ),
});

const partnershipActions = (
  onEditGroups: (partnership: PartnershipRow) => void,
  onEnd: (partnership: PartnershipRow) => void
): TableColumn => ({
  field: 'id',
  text: 'PartnershipScene:actions',
  formatter: (data: PartnershipRow) => (
    <PartnershipActions partnership={data} onEditGroups={onEditGroups} onEnd={onEnd} />
  ),
});

export const partnershipColumns = {
  partnerName,
  invitedAt,
  startedAt,
  groupsSummary,
  inviteActions,
  partnershipActions,
};
